import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, LicenseService } from '@isophi/core-legacy';
import { IconService, SidebarService } from '@isophi/core-ui';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, filter, map, Observable, shareReplay, Subscription } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { MenuTypeEnum } from '../../../core/enums/menu-type.enum';
import { HelperService } from '../../../core/services/helper.service';
import { UserService } from '../../../core/services/user.service';
import { SwitchToDemoKindergartenService } from '../../../shared/services/switch-to-demo-kindergarten.service';

type HeaderBreakpoint = 'mobile' | 'tablet' | 'desktop';
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent implements OnDestroy {
  protected readonly sidebarService = inject(SidebarService);
  private readonly licenseService = inject(LicenseService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  showBackButton = false;

  private showDemoKindergarten = false;

  currentUrl = '';

  data;

  isDirector = this.authService.loggedUser?.teacher.isDirector;

  protected readonly showGroupAndChildrenFilter$ = this.helperService.showGroupAndChildrenFilter$.pipe(
    map((values) => {
      // TEMP disable children select in all cases
      return {
        ...values,
        showChildren: false,
      };
    })
  );

  get title$(): BehaviorSubject<string> {
    return this.helperService.titleChanged$;
  }

  get smallTextTitle$(): BehaviorSubject<string> {
    return this.helperService.smallTextTitle$;
  }

  get showSubMenu$(): BehaviorSubject<{ show: boolean; menuType: MenuTypeEnum }> {
    this.data = this.helperService.data;
    return this.helperService.showSubMenu$;
  }
  isProduction = environment.production;

  private _subs: Subscription[] = [];

  protected readonly bottomContentExpanded$ = new BehaviorSubject(false);

  protected readonly breakpoint$: Observable<HeaderBreakpoint> = this.breakpointObserver
    .observe(['(min-width: 1400px)', '(min-width: 992px)'])
    .pipe(
      map((breakpoints) => {
        let breakpoint: HeaderBreakpoint = 'mobile';

        if (breakpoints.breakpoints['(min-width: 992px)']) {
          breakpoint = 'tablet';
        }
        if (breakpoints.breakpoints['(min-width: 1200px)']) {
          breakpoint = 'desktop';
        }
        return breakpoint;
      }),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  constructor(
    public iconService: IconService,
    public helperService: HelperService,
    private router: Router,
    public switchDemoKindergartenService: SwitchToDemoKindergartenService,
    private userService: UserService,
    protected spinner: NgxSpinnerService,
    private authService: AuthService
  ) {
    // TODO: this doesn't work when paste URL directly to browser
    this._subs.push(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;

        // TODO: for now we don't have back button
        // if (event.url.includes('attendance') || event.url.includes('user') || event.url.includes('settings')) {
        //   this.showBackButton = event.url.split('/').slice(1).length > 2;
        // } else {
        //   this.showBackButton = event.url.split('/').length > 2;
        // }
      })
    );

    this.licenseService.canSeeDemoKindergarten.subscribe((show) => {
      this.showDemoKindergarten = show;
    });
  }

  back(): void {
    // Split the URL into segments
    const segments = this.currentUrl.split('/').filter((segment) => segment);

    // Remove the last segment to navigate one level back
    segments.pop(); // Removes the last segment, e.g., the ID

    if (
      segments.includes('edit-close-relative') ||
      ((segments.includes('gradebook') || segments.includes('announcement') || segments.includes('holidays-dates')) &&
        this.currentUrl.includes('edit')) ||
      segments.includes('edit-user')
    ) {
      segments.pop();
    }

    // Construct the new URL
    const newUrl = '/' + segments.join('/');

    // Use navigateByUrl to navigate
    this.router.navigateByUrl(newUrl);
  }

  get showDemoSwitch(): boolean {
    return !this.userService.loggedKindergarten?.isDemo && this.showDemoKindergarten;
  }

  ngOnDestroy(): void {
    this._subs.forEach((_sub) => _sub.unsubscribe());
  }
}
